import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from "@mui/material";
import { DriveFileRenameOutline } from "@mui/icons-material";
import { renameTrainingGroup } from "./api";
import { getTrainingGroupId } from "./types";
import type { TrainingGroup } from "./types";
import DialogHeader from "./DialogHeader";

interface RenameGroupDialogProps {
  target: { clubId: number; group: TrainingGroup } | null;
  onClose: () => void;
  onSuccess: (groupKey: string) => Promise<void> | void;
  onError: (message: string) => void;
}

const RenameGroupDialog: React.FC<RenameGroupDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    if (target) setName(target.group.group_name);
  }, [target]);

  const handleRename = async () => {
    if (!target) return;
    const trainingGroupId = getTrainingGroupId(target.group);
    const trimmed = name.trim();
    if (trainingGroupId == null || !trimmed) return;

    try {
      setRenaming(true);
      await renameTrainingGroup(trainingGroupId, trimmed);
      await onSuccess(`${target.clubId}-${trimmed}`);
      onClose();
    } catch (err) {
      console.error("Error renaming training group:", err);
      onError("Failed to rename training group. Please try again.");
    } finally {
      setRenaming(false);
    }
  };

  const unchanged = name.trim() === target?.group.group_name;

  return (
    <Dialog
      open={target != null}
      onClose={() => !renaming && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Rename Training Group"
        icon={<DriveFileRenameOutline color="primary" />}
        onClose={onClose}
        closeDisabled={renaming}
      />
      <DialogContent dividers>
        <TextField
          fullWidth
          label="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Tuesday Shadows"
          variant="outlined"
          disabled={renaming}
          sx={{ mt: 1 }}
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={renaming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleRename}
          disabled={renaming || !name.trim() || unchanged}
          startIcon={
            renaming ? <CircularProgress size={20} /> : <DriveFileRenameOutline />
          }
        >
          {renaming ? "Saving…" : "Save Name"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameGroupDialog;
